import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShieldAlert, ShieldBan, ShieldCheck, Search, Plus, Ban, Unlock, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import {
  PageHeader, Tabs, StatCard, Card, CardHeader, DataTable, Badge, Input, Button, Modal, Field, ConfirmDialog, EmptyState,
} from '../../ui';

const PAGE_SIZE = 25;
const SEVERITY_TONE = { critical: 'danger', high: 'danger', medium: 'warning', low: 'neutral' };

export default function SecurityPage() {
  const { t, fmt } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'attacks';
  const [stats, setStats] = useState(null);
  const [banOpen, setBanOpen] = useState(false);
  const [whitelistOpen, setWhitelistOpen] = useState(false);
  const [version, setVersion] = useState(0);
  const refresh = () => setVersion((v) => v + 1);

  useEffect(() => {
    api.get('/admin/security/stats').then((res) => setStats(res.data)).catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [version, t]);

  return (
    <>
      <PageHeader
        title={t('security.title')}
        description={t('security.subtitle')}
        actions={
          <>
            <Button variant="secondary" icon={ShieldCheck} onClick={() => setWhitelistOpen(true)}>{t('security.whitelistAdd')}</Button>
            <Button variant="danger" icon={Ban} onClick={() => setBanOpen(true)}>{t('security.ban')}</Button>
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label={t('security.totalAttacks')} value={stats ? fmt.number(stats.stats.totalAttacks) : '—'} icon={ShieldAlert} />
        <StatCard label={t('security.today')} value={stats ? fmt.number(stats.stats.todayAttacks) : '—'} icon={ShieldAlert} tone={stats?.stats.todayAttacks ? 'warning' : 'neutral'} />
        <StatCard label={t('security.banned')} value={stats ? fmt.number(stats.stats.totalBanned) : '—'} icon={ShieldBan} />
      </div>

      {stats?.topAttackTypes?.length > 0 && (
        <Card className="mt-6">
          <CardHeader title={t('security.topTypes')} />
          <div className="flex flex-wrap gap-2 px-5 py-4">
            {stats.topAttackTypes.map((a) => (
              <span key={a.type} className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm dark:border-zinc-700">
                <span className="font-mono text-zinc-800 dark:text-zinc-200">{a.type}</span>
                <span className="tabular text-xs text-zinc-500">{fmt.number(a.count)}</span>
              </span>
            ))}
          </div>
        </Card>
      )}

      <Tabs
        className="mb-5 mt-8"
        value={tab}
        onChange={(v) => setParams(v === 'attacks' ? {} : { tab: v }, { replace: true })}
        items={[
          { id: 'attacks', label: t('security.tabs.attacks'), icon: ShieldAlert },
          { id: 'banned', label: t('security.tabs.banned'), icon: ShieldBan, count: stats?.stats.totalBanned },
          { id: 'whitelist', label: t('security.tabs.whitelist'), icon: ShieldCheck },
        ]}
      />

      {tab === 'attacks' && <AttacksTab version={version} onChanged={refresh} />}
      {tab === 'banned' && <BannedTab version={version} onChanged={refresh} />}
      {tab === 'whitelist' && <WhitelistTab version={version} onChanged={refresh} />}

      <IpModal
        open={banOpen}
        onClose={() => setBanOpen(false)}
        title={t('security.banTitle')}
        withReason
        submitLabel={t('security.ban')}
        onSubmit={async ({ ip, reason }) => {
          await api.post('/admin/security/ban-ip', { ip, reason: reason || t('security.manualBan') });
          toast.success(t('security.banned_ok'));
          refresh();
        }}
      />
      <IpModal
        open={whitelistOpen}
        onClose={() => setWhitelistOpen(false)}
        title={t('security.whitelistTitle')}
        description={t('security.whitelistHint')}
        submitLabel={t('common.add')}
        onSubmit={async ({ ip }) => {
          await api.post('/admin/security/whitelist-ip', { ip });
          toast.success(t('security.whitelisted_ok'));
          refresh();
        }}
      />
    </>
  );
}

function AttacksTab({ version, onChanged }) {
  const { t, fmt } = useI18n();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [ip, setIp] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(ip.trim()); setPage(1); }, 400);
    return () => clearTimeout(id);
  }, [ip]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/security/logs', { params: { page, limit: PAGE_SIZE, ...(debounced ? { ip: debounced } : {}) } });
      setRows(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [page, debounced, t]);

  useEffect(() => { load(); }, [load, version]);

  const ban = async (address) => {
    try {
      await api.post('/admin/security/ban-ip', { ip: address, reason: t('security.manualBan') });
      toast.success(t('security.banned_ok'));
      onChanged();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  };

  const columns = [
    { key: 'created_at', header: t('security.when'), render: (r) => <span className="tabular text-zinc-500" title={fmt.dateTime(r.created_at)}>{fmt.relative(r.created_at)}</span> },
    {
      key: 'ip_address',
      header: t('security.ip'),
      render: (r) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-zinc-900 dark:text-zinc-100">{r.ip_address}</span>
          {r.is_banned ? <Badge tone="danger">{t('security.isBanned')}</Badge> : null}
        </div>
      ),
    },
    {
      key: 'type',
      header: t('security.type'),
      render: (r) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{r.type}</span>
          {r.severity && <Badge tone={SEVERITY_TONE[r.severity] ?? 'neutral'}>{r.severity}</Badge>}
        </div>
      ),
    },
    {
      key: 'url',
      header: t('security.request'),
      hideOnMobile: true,
      render: (r) => <code className="block max-w-xs truncate font-mono text-xs text-zinc-500" title={r.url}>{r.method} {r.url}</code>,
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'right',
      render: (r) => (r.is_banned ? null : (
        <Button variant="danger-ghost" size="sm" icon={Ban} onClick={() => ban(r.ip_address)}>{t('security.ban')}</Button>
      )),
    },
  ];

  return (
    <>
      <div className="mb-4 sm:w-72">
        <Input icon={Search} value={ip} onChange={(e) => setIp(e.target.value)} placeholder={t('security.filterIp')} aria-label={t('security.filterIp')} className="font-mono" />
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        empty={<EmptyState icon={ShieldCheck} title={t('security.emptyAttacks')} />}
        pagination={{ page, pages: Math.max(1, Math.ceil(total / PAGE_SIZE)), onPage: setPage }}
      />
    </>
  );
}

function BannedTab({ version, onChanged }) {
  const { t, fmt } = useI18n();
  const [rows, setRows] = useState(null);
  const [unbanning, setUnbanning] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/admin/security/banned-ips', { params: { page: 1, limit: 200 } })
      .then((res) => setRows(res.data.items || []))
      .catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [version, t]);

  const unban = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/security/unban-ip/${unbanning.id}`);
      toast.success(t('security.unbanned_ok'));
      setUnbanning(null);
      onChanged();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'ip_address', header: t('security.ip'), sortable: true, render: (r) => <span className="font-mono text-zinc-900 dark:text-zinc-100">{r.ip_address}</span> },
    { key: 'reason', header: t('security.reason'), render: (r) => <span className="block max-w-md truncate text-zinc-600 dark:text-zinc-400" title={r.reason}>{r.reason || '—'}</span> },
    { key: 'attempt_count', header: t('security.attempts'), sortable: true, align: 'right', render: (r) => <span className="tabular">{fmt.number(r.attempt_count)}</span> },
    { key: 'created_at', header: t('security.when'), sortable: true, hideOnMobile: true, render: (r) => <span className="tabular text-zinc-500">{fmt.dateTime(r.created_at)}</span> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'right',
      render: (r) => <Button variant="ghost" size="sm" icon={Unlock} onClick={() => setUnbanning(r)}>{t('security.unban')}</Button>,
    },
  ];

  return (
    <>
      <DataTable columns={columns} rows={rows || []} loading={!rows} empty={<EmptyState icon={ShieldBan} title={t('security.emptyBanned')} />} />
      <ConfirmDialog
        open={!!unbanning}
        onClose={() => setUnbanning(null)}
        onConfirm={unban}
        loading={busy}
        tone="neutral"
        title={t('security.unbanConfirm', { ip: unbanning?.ip_address })}
        text={t('security.unbanText')}
        confirmLabel={t('security.unban')}
      />
    </>
  );
}

function WhitelistTab({ version, onChanged }) {
  const { t, fmt } = useI18n();
  const [rows, setRows] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/admin/security/whitelisted-ips')
      .then((res) => setRows(res.data.ips || []))
      .catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [version, t]);

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/security/whitelist-ip/${encodeURIComponent(removing.ip_address)}`);
      toast.success(t('security.removed_ok'));
      setRemoving(null);
      onChanged();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'ip_address', header: t('security.ip'), sortable: true, render: (r) => <span className="font-mono text-zinc-900 dark:text-zinc-100">{r.ip_address}</span> },
    { key: 'created_at', header: t('common.created'), sortable: true, render: (r) => <span className="tabular text-zinc-500">{fmt.dateTime(r.created_at)}</span> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'right',
      render: (r) => <Button variant="danger-ghost" size="sm" icon={Trash2} onClick={() => setRemoving(r)}>{t('security.remove')}</Button>,
    },
  ];

  return (
    <>
      <DataTable columns={columns} rows={rows || []} loading={!rows} empty={<EmptyState icon={ShieldCheck} title={t('security.emptyWhitelist')} />} />
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        loading={busy}
        title={t('security.removeConfirm', { ip: removing?.ip_address })}
        text={t('security.removeText')}
        confirmLabel={t('security.remove')}
      />
    </>
  );
}

function IpModal({ open, onClose, title, description, withReason, submitLabel, onSubmit }) {
  const { t } = useI18n();
  const [ip, setIp] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setIp(''); setReason(''); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({ ip: ip.trim(), reason: reason.trim() });
      onClose();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="ip-form" loading={saving} icon={withReason ? Ban : Plus} variant={withReason ? 'danger' : 'primary'}>{submitLabel}</Button>
        </>
      }
    >
      <form id="ip-form" onSubmit={submit} className="space-y-4">
        <Field label={t('security.ip')} required>
          <Input value={ip} onChange={(e) => setIp(e.target.value)} required placeholder="203.0.113.7" className="font-mono" autoFocus />
        </Field>
        {withReason && (
          <Field label={t('security.reason')}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
          </Field>
        )}
      </form>
    </Modal>
  );
}
